import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { AlertMessagePreview } from '@/components/alerts/AlertMessagePreview';
import { ChannelChip } from '@/components/alerts/ChannelChip';
import {
  CONDITIONS,
  GROUP_BY_OPTIONS,
  SEVERITIES,
  THROTTLE_OPTIONS,
} from '@/components/alerts/conditions';
import type {
  AlertGroupBy,
  AlertSeverity,
  NotificationChannel,
  NotificationCondition,
  NotificationRule,
  RuleParameters,
  WorkflowSummary,
} from '@/types/sturm';

interface RuleEditorProps {
  rule: NotificationRule | null;
  lockedScope?: string;
  channels: NotificationChannel[];
  workflows: WorkflowSummary[];
  onClose: () => void;
  onSave: (draft: Partial<NotificationRule>) => void;
  isSaving?: boolean;
}

function blankRule(lockedScope?: string): Partial<NotificationRule> {
  return {
    name: '',
    isEnabled: true,
    condition: 'run-failed',
    scope: lockedScope ? [lockedScope] : ['*'],
    parameters: {},
    severity: 'critical',
    groupBy: 'workflow',
    throttle: '15m',
    autoResolve: true,
    channelIds: [],
  };
}

export function RuleEditor({
  rule,
  lockedScope,
  channels,
  workflows,
  onClose,
  onSave,
  isSaving,
}: RuleEditorProps) {
  const [draft, setDraft] = useState<Partial<NotificationRule>>(rule ?? blankRule(lockedScope));

  const set = (patch: Partial<NotificationRule>) => setDraft((d) => ({ ...d, ...patch }));
  const setParam = (patch: RuleParameters) =>
    setDraft((d) => ({ ...d, parameters: { ...d.parameters, ...patch } }));

  const toggleChannel = (id: string) => {
    const current = draft.channelIds ?? [];
    set({ channelIds: current.includes(id) ? current.filter((c) => c !== id) : [...current, id] });
  };

  const toggleScope = (slug: string) => {
    if (slug === '*') {
      set({ scope: ['*'] });
      return;
    }

    const current = (draft.scope ?? []).filter((s) => s !== '*');
    const next = current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug];
    set({ scope: next.length > 0 ? next : ['*'] });
  };

  const previewWorkflow = lockedScope
    ? workflows.find((w) => w.slug === lockedScope)
    : workflows.find((w) => draft.scope?.includes(w.slug));

  const canSave = Boolean(draft.name?.trim()) && (draft.channelIds?.length ?? 0) > 0;
  const stepNumber = (n: number) => (lockedScope ? n - 1 : n);

  return (
    <div className="card mb-24" style={{ borderColor: 'var(--ink-300)', boxShadow: 'var(--shadow-md)' }}>
      <div className="card-hd">
        <Icon name="bell" size={15} />
        <div className="flex1">
          <div className="card-hd-title">{rule ? 'Edit notification rule' : 'New notification rule'}</div>
          <div className="card-hd-sub">
            Decide what counts as a problem, who hears about it, and how loud it gets.
          </div>
        </div>
        <button className="btn btn-icon btn-sm btn-ghost" onClick={onClose}>
          <Icon name="close" size={14} />
        </button>
      </div>

      <div className="card-pad stack gap-24">
        <div className="field" style={{ maxWidth: 420 }}>
          <label className="field-label">Rule name</label>
          <input
            className="input"
            placeholder="Ledger posting failures"
            value={draft.name ?? ''}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>

        <EditorStep
          number={1}
          title="When this happens"
          sub="Conditions are evaluated on the run stream, not on a polling schedule — a failure notifies within a second of the last retry."
        >
          <div className="ed-grid mt-12">
            {CONDITIONS.map((condition) => (
              <button
                key={condition.id}
                className={`pick-btn ${draft.condition === condition.id ? 'is-active' : ''}`}
                onClick={() => set({ condition: condition.id as NotificationCondition, parameters: {} })}
              >
                <Icon name={condition.icon} size={15} />
                <div style={{ textAlign: 'left' }}>
                  <div className="pick-title">{condition.name}</div>
                  <div className="pick-sub">{condition.sub}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="row gap-8 mt-16" style={{ flexWrap: 'wrap' }}>
            <ConditionParams condition={draft.condition} parameters={draft.parameters ?? {}} onChange={setParam} />
          </div>
        </EditorStep>

        {!lockedScope ? (
          <EditorStep
            number={2}
            title="On these workflows"
            sub="A rule can watch everything, one team's workflows, or a single one."
          >
            <div className="row gap-6 mt-12" style={{ flexWrap: 'wrap' }}>
              <span
                className={`chan is-pick ${draft.scope?.[0] === '*' ? 'is-on' : ''}`}
                onClick={() => toggleScope('*')}
              >
                <Icon name="matrix" size={12} />
                All workflows
              </span>
              {workflows.map((w) => (
                <span
                  key={w.slug}
                  className={`chan is-pick ${draft.scope?.includes(w.slug) ? 'is-on' : ''}`}
                  onClick={() => toggleScope(w.slug)}
                >
                  <Icon name="flow" size={12} />
                  {w.name}
                </span>
              ))}
            </div>
          </EditorStep>
        ) : null}

        <EditorStep
          number={stepNumber(3)}
          title="Tell these people"
          sub="Severity decides whether it pages. Channels decide where it lands."
        >
          <div className="row gap-8 mt-12" style={{ flexWrap: 'wrap' }}>
            {SEVERITIES.map((severity) => (
              <button
                key={severity.id}
                className={`pick-btn ${draft.severity === severity.id ? 'is-active' : ''}`}
                style={{ flex: 1, minWidth: 170 }}
                onClick={() => set({ severity: severity.id as AlertSeverity })}
              >
                <span className={`feed-dot ${severity.id}`} style={{ marginTop: 0 }} />
                <div style={{ textAlign: 'left' }}>
                  <div className="pick-title">{severity.label}</div>
                  <div className="pick-sub">{severity.sub}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="row gap-6 mt-12" style={{ flexWrap: 'wrap' }}>
            {channels.map((channel) => (
              <ChannelChip
                key={channel.id}
                channel={channel}
                on={draft.channelIds?.includes(channel.id)}
                onClick={() => toggleChannel(channel.id)}
              />
            ))}
            {channels.length === 0 ? (
              <span className="tiny muted">No channels are configured yet.</span>
            ) : null}
          </div>
        </EditorStep>

        <EditorStep
          number={stepNumber(4)}
          title="Keep it quiet enough to be believed"
          sub="The reason people ignore alerts is volume. Grouping and throttling are part of the rule, not an afterthought."
        >
          <div className="grid-3 mt-12">
            <div className="field">
              <label className="field-label">Group notifications by</label>
              <select
                className="select"
                value={draft.groupBy}
                onChange={(e) => set({ groupBy: e.target.value as AlertGroupBy })}
              >
                {GROUP_BY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="field-help">
                200 runs failing on one bad deploy becomes one message that counts up.
              </div>
            </div>
            <div className="field">
              <label className="field-label">Throttle</label>
              <select
                className="select"
                value={draft.throttle}
                onChange={(e) => set({ throttle: e.target.value })}
              >
                {THROTTLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="field-help">
                Repeats update the same message instead of posting a new one.
              </div>
            </div>
            <div className="field">
              <label className="field-label">Resolution</label>
              <label className="row gap-8" style={{ cursor: 'pointer', height: 36 }}>
                <button
                  className={`switch sm ${draft.autoResolve ? 'on' : ''}`}
                  onClick={() => set({ autoResolve: !draft.autoResolve })}
                >
                  <span className="knob" />
                </button>
                <span style={{ fontSize: 13 }}>Auto-resolve when healthy again</span>
              </label>
              <div className="field-help">Posts a follow-up and closes the alert after 3 clean runs.</div>
            </div>
          </div>
        </EditorStep>

        <div>
          <div className="editor-preview-label">Preview — what gets delivered</div>
          <AlertMessagePreview rule={draft} channels={channels} workflow={previewWorkflow} />
        </div>
      </div>

      <div className="modal-foot" style={{ borderTop: '1px solid var(--ink-200)' }}>
        <div className="spread">Rules apply to runs that start after saving.</div>
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-accent" onClick={() => onSave(draft)} disabled={!canSave || isSaving}>
          <Icon name="check" size={14} /> {rule ? 'Save rule' : 'Create rule'}
        </button>
      </div>
    </div>
  );
}

function EditorStep({
  number,
  title,
  sub,
  children,
}: {
  number: number;
  title: string;
  sub: string;
  children: React.ReactNode;
}) {
  return (
    <div className="editor-step">
      <div className="editor-step-num" style={{ background: 'var(--ink-1000)', color: 'var(--white)' }}>
        {number}
      </div>
      <div className="flex1">
        <div className="editor-step-title">{title}</div>
        <div className="editor-step-sub">{sub}</div>
        {children}
      </div>
    </div>
  );
}

function ConditionParams({
  condition,
  parameters,
  onChange,
}: {
  condition: NotificationCondition | undefined;
  parameters: RuleParameters;
  onChange: (patch: RuleParameters) => void;
}) {
  const num = (value: string | number, onInput: (v: string) => void, width = 74) => (
    <input className="num-in" style={{ width }} value={value} onChange={(e) => onInput(e.target.value)} />
  );

  switch (condition) {
    case 'step-retrying':
      return (
        <>
          <span className="tiny muted">Alert after</span>
          {num(parameters.attempts ?? 3, (v) => onChange({ attempts: Number(v) || 0 }))}
          <span className="tiny muted">attempts on the same step</span>
        </>
      );
    case 'run-stuck':
      return (
        <>
          <span className="tiny muted">Alert when a run has been going for</span>
          {num(parameters.runningLongerThan ?? '1h', (v) => onChange({ runningLongerThan: v }))}
        </>
      );
    case 'failure-rate':
      return (
        <>
          <span className="tiny muted">Above</span>
          {num(parameters.failurePercent ?? 10, (v) => onChange({ failurePercent: Number(v) || 0 }), 58)}
          <span className="tiny muted">% failures over</span>
          {num(parameters.window ?? '30m', (v) => onChange({ window: v }), 66)}
        </>
      );
    case 'schedule-missed':
      return (
        <>
          <span className="tiny muted">Grace period after the expected start</span>
          {num(parameters.grace ?? '10m', (v) => onChange({ grace: v }))}
        </>
      );
    case 'queue-backlog':
      return (
        <>
          <span className="tiny muted">More than</span>
          {num(parameters.queuedTasks ?? 200, (v) => onChange({ queuedTasks: Number(v) || 0 }))}
          <span className="tiny muted">tasks waiting for</span>
          {num(parameters.sustainedFor ?? '10m', (v) => onChange({ sustainedFor: v }), 66)}
        </>
      );
    case 'run-failed':
      return (
        <span className="ed-note">
          Fires once the retry budget is exhausted, so a step that recovers on attempt 3 stays quiet.
          Narrow it to specific steps below if only part of the workflow matters.
        </span>
      );
    case 'terminated':
      return (
        <span className="ed-note">
          Covers human intervention — cancels and terminates — so the audit trail always has a
          notification attached.
        </span>
      );
    default:
      return null;
  }
}
